import React from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import {
  ShieldCheck, Building2, BadgeCheck, Sparkles, GitBranch,
  Workflow, Users, FileText, Banknote, Globe, Lock,
  ArrowRight, ChevronDown, CheckCircle2,
} from "lucide-react";

const CREDS = [
  { role: "Super Admin (Multi-tenant)", email: "superadmin@acas.com", password: "super123", icon: Globe },
  { role: "HO Admin", email: "admin@acas.bank", password: "admin123", icon: ShieldCheck },
  { role: "Branch Manager", email: "manager@acas.bank", password: "manager123", icon: Building2 },
  { role: "Credit Officer", email: "credit@acas.bank", password: "credit123", icon: BadgeCheck },
  { role: "Field Officer", email: "officer@acas.bank", password: "officer123", icon: Users },
  { role: "Regional Head", email: "regional@acas.bank", password: "regional123", icon: GitBranch },
];

const HIGHLIGHTS = [
  { icon: Workflow, title: "End-to-End Origination", desc: "Lead capture → KYC → CMA/CRA → Sanction → Disbursement, all in one workflow." },
  { icon: Lock, title: "Bank-Grade Security", desc: "AES-GCM encryption at rest, JWT auth, RBAC, Maker-Checker on every critical action." },
  { icon: GitBranch, title: "Multi-Tenant SaaS", desc: "One platform — multiple banks, isolated data, per-bank branding & configuration." },
  { icon: Sparkles, title: "Auto Engine", desc: "Smart OCR for financial statements, auto-CMA, auto-CRA scoring & S-Format proposals." },
  { icon: FileText, title: "Configurable Products", desc: "No-code Loan Product Master with Risk-Based Pricing matrix per product." },
  { icon: Banknote, title: "Disbursement Pipeline", desc: "13-point readiness checklist + NEFT/RTGS gateway integration." },
];

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email || !password) {
      toast.error("Email and password are required");
      return;
    }
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome to Traceroot");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const fillDemo = (c) => {
    setEmail(c.email);
    setPassword(c.password);
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-5 bg-background">
      {/* Left marketing panel — 3/5 */}
      <div className="hidden lg:flex lg:col-span-3 flex-col justify-between p-10 xl:p-14 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, hsl(221 70% 12%) 0%, hsl(221 60% 18%) 50%, hsl(186 60% 22%) 100%)" }}>
        {/* Animated background pattern */}
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, white 0.5px, transparent 0.5px), radial-gradient(circle at 80% 80%, white 0.5px, transparent 0.5px)", backgroundSize: "32px 32px, 24px 24px" }} />
        {/* Soft glow */}
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full opacity-30 blur-3xl" style={{ background: "radial-gradient(circle, hsl(186 80% 50%) 0%, transparent 70%)" }} />
        <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full opacity-25 blur-3xl" style={{ background: "radial-gradient(circle, hsl(221 80% 55%) 0%, transparent 70%)" }} />

        <div className="relative z-10">
          {/* Brand row */}
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center backdrop-blur-sm shadow-lg">
              <GitBranch className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="text-xl font-bold heading tracking-tight" data-testid="brand-traceroot">Traceroot</div>
              <div className="text-xs text-white/65 tracking-wide">Unified Loan Origination System</div>
            </div>
          </div>

          {/* Hero text */}
          <div className="mt-16 max-w-2xl">
            <Badge variant="outline" className="bg-white/8 border-white/20 text-white/90 backdrop-blur-sm mb-4 text-[11px] tracking-wide">
              <Sparkles className="h-3 w-3 mr-1.5" />
              Built for India's small &amp; mid-size banks
            </Badge>
            <h1 className="text-3xl xl:text-5xl font-bold heading leading-[1.1] tracking-tight">
              The complete loan platform — <span className="text-accent">from lead to disbursement</span>.
            </h1>
            <p className="mt-4 text-white/75 text-base leading-relaxed max-w-xl">
              One unified workflow for Personal, Housing, Gold, Vehicle, SME and MSME loans. Configurable per product, scoped per tenant, secured to bank-grade standards.
            </p>
          </div>

          {/* Feature highlights — 2x3 grid */}
          <div className="mt-12 grid grid-cols-2 gap-3 max-w-2xl">
            {HIGHLIGHTS.map((h, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-4 hover:bg-white/[0.08] hover:border-white/20 transition-all duration-200">
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg bg-accent/15 border border-accent/30 flex items-center justify-center shrink-0">
                    <h.icon className="h-4 w-4 text-accent" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-white leading-tight">{h.title}</div>
                    <div className="text-xs text-white/65 mt-0.5 leading-relaxed">{h.desc}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer attribution */}
        <div className="relative z-10 mt-10 flex items-end justify-between gap-4">
          <div className="text-xs text-white/55 mono space-y-1">
            <div>v3.0 · Multi-Tenant · 6 languages · CMA/CRA · Maker-Checker</div>
            <div className="text-[11px] text-white/45">
              Developed &amp; maintained by{" "}
              <a
                href="https://bharatcores.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-white/80 hover:text-white underline decoration-dotted underline-offset-2"
                data-testid="bcs-link"
              >
                Bharat Cores Solutions Private Limited
              </a>
            </div>
          </div>
          <div className="hidden xl:flex flex-col items-center text-white/40 text-[10px]">
            <ChevronDown className="h-4 w-4 animate-bounce" />
            <span>Scroll for credentials</span>
          </div>
        </div>
      </div>

      {/* Right login panel — 2/5 */}
      <div className="lg:col-span-2 flex items-center justify-center p-6 lg:p-8 relative">
        <div className="w-full max-w-md">
          {/* Mobile brand */}
          <div className="lg:hidden mb-6 flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shadow">
              <GitBranch className="h-5 w-5" />
            </div>
            <div>
              <div className="font-bold heading text-lg">Traceroot</div>
              <div className="text-[11px] text-muted-foreground tracking-wide">Unified LOS</div>
            </div>
          </div>

          <div className="flex items-start justify-between gap-2 mb-1">
            <div>
              <h2 className="text-2xl font-semibold heading tracking-tight">{t("login.title", "Welcome back")}</h2>
              <p className="text-sm text-muted-foreground mt-1">{t("login.subtitle", "Sign in to your bank's workspace")}</p>
            </div>
            <LanguageSwitcher variant="compact" />
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{t("login.email", "Email")}</Label>
              <Input
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                placeholder="you@yourbank.com"
                data-testid="login-email-input"
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{t("login.password", "Password")}</Label>
              <Input
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                placeholder="••••••••"
                data-testid="login-password-input"
                className="h-11"
              />
            </div>
            <Button type="submit" disabled={busy} className="w-full h-11" data-testid="login-submit-button">
              {busy ? t("app.loading", "Signing in…") : <>Sign In<ArrowRight className="h-4 w-4 ml-1.5" /></>}
            </Button>
            <div className="text-[11px] text-center text-muted-foreground flex items-center justify-center gap-1.5">
              <Lock className="h-3 w-3" />
              Protected by bank-grade encryption · Optional Google 2FA available after sign-in
            </div>
          </form>

          {/* Demo creds toggle */}
          <Card className="mt-6 p-4 border-dashed">
            <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2.5 flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-accent" />
              {t("login.demo", "Try a demo account")}
            </div>
            <div className="space-y-1">
              {CREDS.map((c, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => fillDemo(c)}
                  className="w-full text-left text-sm rounded-lg px-3 py-2 hover:bg-accent/10 border border-transparent hover:border-accent/30 flex items-center justify-between gap-2 group transition-colors"
                  data-testid={`login-demo-${c.email}`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="h-7 w-7 rounded-md bg-muted/60 flex items-center justify-center shrink-0">
                      <c.icon className="h-3.5 w-3.5 text-muted-foreground group-hover:text-accent" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium leading-tight truncate">{c.role}</div>
                      <div className="text-[11px] text-muted-foreground mono truncate">{c.email}</div>
                    </div>
                  </div>
                  <span className="text-[11px] text-accent opacity-0 group-hover:opacity-100 transition-opacity shrink-0">Use →</span>
                </button>
              ))}
            </div>
          </Card>

          <div className="lg:hidden mt-6 text-center text-[11px] text-muted-foreground">
            Developed &amp; maintained by{" "}
            <a href="https://bharatcores.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-foreground hover:text-accent underline decoration-dotted">
              Bharat Cores Solutions Pvt Ltd
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
